import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import configuratorService from '../services/configuratorService';

// ==========================================
// Styled Components
// ==========================================

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const DateInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &.highlight {
    background: linear-gradient(135deg, #3b82f6, #1d4ed8);
    color: white;
  }
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: ${props => props.light ? 'rgba(255,255,255,0.8)' : '#6b7280'};
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.light ? 'white' : '#1f2937'};
`;

const StatTrend = styled.span`
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
  background: ${props => props.positive ? '#dcfce7' : props.negative ? '#fee2e2' : '#f3f4f6'};
  color: ${props => props.positive ? '#16a34a' : props.negative ? '#dc2626' : '#6b7280'};
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || '1fr 1fr'};
  gap: 24px;
  margin-bottom: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const CardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FunnelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FunnelStep = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FunnelBar = styled.div`
  height: 36px;
  background: ${props => props.color || '#e5e7eb'};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  color: white;
  font-weight: 500;
  min-width: ${props => Math.max(props.width || 0, 10)}%;
  transition: min-width 0.3s ease;
`;

const FunnelLabel = styled.span`
  min-width: 120px;
  font-size: 14px;
  color: #374151;
`;

const FunnelValue = styled.span`
  font-size: 13px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #f3f4f6;
  }
  
  th {
    font-size: 12px;
    text-transform: uppercase;
    color: #6b7280;
    font-weight: 600;
  }
  
  td {
    font-size: 14px;
    color: #374151;
  }
  
  tr:hover td {
    background: #f9fafb;
  }
`;

const Badge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.bg || '#f3f4f6'};
  color: ${props => props.color || '#374151'};
`;

const ChartPlaceholder = styled.div`
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  border-radius: 8px;
  color: #9ca3af;
  font-size: 14px;
`;

const SimpleChart = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 150px;
  padding: 16px 0;
`;

const ChartBar = styled.div`
  flex: 1;
  min-width: 20px;
  background: linear-gradient(180deg, #3b82f6, #1d4ed8);
  border-radius: 4px 4px 0 0;
  height: ${props => props.height}%;
  transition: height 0.3s ease;
  position: relative;
  
  &:hover {
    background: linear-gradient(180deg, #60a5fa, #3b82f6);
  }
  
  &:hover::after {
    content: '${props => props.label}';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: #1f2937;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    white-space: nowrap;
    margin-bottom: 4px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #6b7280;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #9ca3af;
`;

// ==========================================
// Main Component
// ==========================================

function ConfiguratorAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [configurators, setConfigurators] = useState([]);
  const [selectedConfigurator, setSelectedConfigurator] = useState('');
  const [dateRange, setDateRange] = useState('30');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Analytics data
  const [dashboard, setDashboard] = useState(null);
  const [popularItems, setPopularItems] = useState([]);
  const [ordersOverTime, setOrdersOverTime] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [funnel, setFunnel] = useState([]);

  useEffect(() => {
    loadConfigurators();
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [selectedConfigurator, dateRange, startDate, endDate]);

  const loadConfigurators = async () => {
    try {
      const data = await configuratorService.getConfigurators(true);
      setConfigurators(data.configurators || []);
    } catch (err) {
      console.error('Failed to load configurators:', err);
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const options = {
        configuratorId: selectedConfigurator || undefined
      };
      
      // Calculate dates based on range
      if (dateRange === 'custom') {
        if (startDate) options.startDate = startDate;
        if (endDate) options.endDate = endDate;
      } else {
        const days = parseInt(dateRange) || 30;
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - days);
        options.startDate = start.toISOString();
        options.endDate = end.toISOString();
      }
      
      // Load all analytics in parallel
      const [dashboardData, itemsData, timeData, perfData, funnelData] = await Promise.all([
        configuratorService.getAnalyticsDashboard(options),
        configuratorService.getPopularItems({ ...options, limit: 10 }),
        configuratorService.getOrdersOverTime({ ...options, granularity: 'day' }),
        configuratorService.getConfiguratorPerformance(options),
        configuratorService.getPipelineFunnel(options)
      ]);
      
      setDashboard(dashboardData);
      setPopularItems(itemsData.items || []);
      setOrdersOverTime(timeData.data || []);
      setPerformance(perfData.configurators || []);
      setFunnel(funnelData.funnel || []);
      
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value, currency = 'EUR') => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency
    }).format(value);
  };

  const formatPercent = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  // Calculate max values for charts
  const maxFunnelCount = Math.max(...funnel.map(f => f.order_count), 1);
  const maxTimeValue = Math.max(...ordersOverTime.map(d => d.total_value), 1);

  if (loading && !dashboard) {
    return (
      <PageContainer>
        <LoadingSpinner>Loading analytics...</LoadingSpinner>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <Title>📊 Configurator Analytics</Title>
        <FilterBar>
          <Select
            value={selectedConfigurator}
            onChange={(e) => setSelectedConfigurator(e.target.value)}
          >
            <option value="">All Configurators</option>
            {configurators.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Select>
          
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
            <option value="custom">Custom range</option>
          </Select>
          
          {dateRange === 'custom' && (
            <>
              <DateInput
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start date"
              />
              <DateInput
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End date"
              />
            </>
          )}
        </FilterBar>
      </PageHeader>

      {/* Overview Stats */}
      <StatsGrid>
        <StatCard className="highlight">
          <StatLabel light>Total Orders</StatLabel>
          <StatValue light>
            {dashboard?.total_orders || 0}
            {dashboard?.trends?.orders !== 0 && (
              <StatTrend positive={dashboard?.trends?.orders > 0} negative={dashboard?.trends?.orders < 0}>
                {formatPercent(dashboard?.trends?.orders || 0)}
              </StatTrend>
            )}
          </StatValue>
        </StatCard>
        
        <StatCard>
          <StatLabel>Total Value</StatLabel>
          <StatValue>
            {formatCurrency(dashboard?.total_value || 0)}
            {dashboard?.trends?.value !== 0 && (
              <StatTrend positive={dashboard?.trends?.value > 0} negative={dashboard?.trends?.value < 0}>
                {formatPercent(dashboard?.trends?.value || 0)}
              </StatTrend>
            )}
          </StatValue>
        </StatCard>
        
        <StatCard>
          <StatLabel>Average Order</StatLabel>
          <StatValue>{formatCurrency(dashboard?.average_value || 0)}</StatValue>
        </StatCard>
        
        <StatCard>
          <StatLabel>Conversion Rate</StatLabel>
          <StatValue>
            {dashboard?.conversion_rate || 0}%
          </StatValue>
        </StatCard>
        
        <StatCard>
          <StatLabel>Won</StatLabel>
          <StatValue style={{ color: '#16a34a' }}>
            {dashboard?.won_count || 0}
            <span style={{ fontSize: '14px', marginLeft: '8px', color: '#6b7280' }}>
              ({formatCurrency(dashboard?.won_value || 0)})
            </span>
          </StatValue>
        </StatCard>
        
        <StatCard>
          <StatLabel>Lost</StatLabel>
          <StatValue style={{ color: '#dc2626' }}>
            {dashboard?.lost_count || 0}
          </StatValue>
        </StatCard>
      </StatsGrid>

      <SectionGrid>
        {/* Pipeline Funnel */}
        <Card>
          <CardTitle>📈 Pipeline Funnel</CardTitle>
          {funnel.length === 0 ? (
            <EmptyState>No pipeline data available</EmptyState>
          ) : (
            <FunnelContainer>
              {funnel.map(step => (
                <FunnelStep key={step.status_id}>
                  <FunnelLabel>{step.status_name}</FunnelLabel>
                  <FunnelBar 
                    color={step.color}
                    width={(step.order_count / maxFunnelCount) * 100}
                  >
                    <span>{step.order_count}</span>
                    <FunnelValue>{formatCurrency(step.total_value)}</FunnelValue>
                  </FunnelBar>
                </FunnelStep>
              ))}
            </FunnelContainer>
          )}
        </Card>

        {/* Orders Over Time */}
        <Card>
          <CardTitle>📅 Orders Over Time</CardTitle>
          {ordersOverTime.length === 0 ? (
            <EmptyState>No data for selected period</EmptyState>
          ) : (
            <SimpleChart>
              {ordersOverTime.slice(-30).map((d, i) => (
                <ChartBar
                  key={i}
                  height={(d.total_value / maxTimeValue) * 100}
                  label={`${new Date(d.date).toLocaleDateString()}: ${formatCurrency(d.total_value)}`}
                />
              ))}
            </SimpleChart>
          )}
        </Card>
      </SectionGrid>

      <SectionGrid>
        {/* Popular Items */}
        <Card>
          <CardTitle>🏆 Most Popular Items</CardTitle>
          {popularItems.length === 0 ? (
            <EmptyState>No items selected yet</EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Section</th>
                  <th>Selections</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {popularItems.map((item, i) => (
                  <tr key={item.item_id || i}>
                    <td>{item.name}</td>
                    <td>
                      <Badge bg="#f3f4f6" color="#374151">
                        {item.section_name || '-'}
                      </Badge>
                    </td>
                    <td>{item.selection_count}</td>
                    <td>{formatCurrency(item.total_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        {/* Configurator Performance */}
        <Card>
          <CardTitle>⚡ Configurator Performance</CardTitle>
          {performance.length === 0 ? (
            <EmptyState>No configurators found</EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Configurator</th>
                  <th>Orders</th>
                  <th>Revenue</th>
                  <th>Conv.</th>
                </tr>
              </thead>
              <tbody>
                {performance.map(cfg => (
                  <tr 
                    key={cfg.id} 
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/configurators/${cfg.id}/edit`)}
                  >
                    <td>
                      {cfg.name}
                      {!cfg.is_active && (
                        <Badge bg="#fee2e2" color="#dc2626" style={{ marginLeft: 8 }}>
                          Inactive
                        </Badge>
                      )}
                    </td>
                    <td>{cfg.total_orders}</td>
                    <td>{formatCurrency(cfg.total_value)}</td>
                    <td>
                      <Badge 
                        bg={cfg.conversion_rate >= 50 ? '#dcfce7' : cfg.conversion_rate >= 25 ? '#fef3c7' : '#fee2e2'}
                        color={cfg.conversion_rate >= 50 ? '#16a34a' : cfg.conversion_rate >= 25 ? '#d97706' : '#dc2626'}
                      >
                        {cfg.conversion_rate}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </SectionGrid>
    </PageContainer>
  );
}

export default ConfiguratorAnalytics;
